import { useEffect, useRef, useState, type DragEvent, type FormEvent, type KeyboardEvent } from 'react'
import {
  ACCEPT_ATTRIBUTE,
  filesFromDataTransfer,
  readDocument,
  triageFiles,
  type AttachedDocument,
  type RejectedFile,
} from '../lib/documents'
import { AttachmentTray } from './AttachmentTray'

interface ComposerProps {
  onSend: (text: string, documents: AttachedDocument[]) => void
  disabled: boolean
  busy: boolean
}

const MAX_ROWS_HEIGHT = 200

export function Composer({ onSend, disabled, busy }: ComposerProps) {
  const [value, setValue] = useState('')
  const [documents, setDocuments] = useState<AttachedDocument[]>([])
  const [parsing, setParsing] = useState<string[]>([])
  const [notice, setNotice] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  const textarea = useRef<HTMLTextAreaElement>(null)
  const filePicker = useRef<HTMLInputElement>(null)
  const folderPicker = useRef<HTMLInputElement>(null)
  const dragDepth = useRef(0)

  useEffect(() => {
    const el = textarea.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MAX_ROWS_HEIGHT)}px`
  }, [value])

  async function attach(incoming: File[]) {
    setNotice(null)
    const { accepted, rejected } = triageFiles(incoming, documents.length + parsing.length)

    if (accepted.length === 0) {
      setNotice(
        rejected.length > 0
          ? rejected.map(describeRejection).join(' ')
          : 'No readable documents found. Attach PDF, Word (.docx), or text files.',
      )
      return
    }

    const names = accepted.map((file) => file.name)
    setParsing((prev) => [...prev, ...names])

    const results = await Promise.allSettled(accepted.map((file) => readDocument(file)))

    setParsing((prev) => prev.filter((name) => !names.includes(name)))

    const parsed = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []))
    setDocuments((prev) => [...prev, ...parsed])

    const problems = [
      ...rejected.map(describeRejection),
      ...results.flatMap((result) => (result.status === 'rejected' ? [messageOf(result.reason)] : [])),
      // Fallback parsing succeeded but produced weaker text; saying so beats
      // letting the agent reason over a silently degraded contract.
      ...parsed.flatMap((doc) => (doc.warning ? [doc.warning] : [])),
    ]
    if (problems.length > 0) setNotice(problems.join(' '))
  }

  function submit(event?: FormEvent) {
    event?.preventDefault()
    const text = value.trim()
    if (disabled || parsing.length > 0) return
    if (!text && documents.length === 0) return

    onSend(text, documents)
    setValue('')
    setDocuments([])
    setNotice(null)
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }

  function onDragEnter(event: DragEvent) {
    if (!event.dataTransfer.types.includes('Files')) return
    dragDepth.current += 1
    setDragging(true)
  }

  function onDragLeave() {
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDragging(false)
  }

  function onDrop(event: DragEvent) {
    event.preventDefault()
    dragDepth.current = 0
    setDragging(false)
    if (disabled) return
    void filesFromDataTransfer(event.dataTransfer).then(attach)
  }

  const canSend =
    !disabled && parsing.length === 0 && (value.trim().length > 0 || documents.length > 0)

  return (
    <div
      className={`composer-shell${dragging ? ' composer-shell--dragging' : ''}`}
      onDragEnter={onDragEnter}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <AttachmentTray
        documents={documents}
        parsing={parsing}
        onRemove={(id) => setDocuments((prev) => prev.filter((doc) => doc.id !== id))}
      />

      {notice && (
        <p className="composer__notice" role="status">
          {notice}
        </p>
      )}

      <form className="composer" onSubmit={submit}>
        <div className="composer__actions">
          <button
            type="button"
            className="composer__attach"
            onClick={() => filePicker.current?.click()}
            disabled={disabled}
            title="Attach files"
            aria-label="Attach files"
          >
            <PaperclipIcon />
          </button>
          <button
            type="button"
            className="composer__attach"
            onClick={() => folderPicker.current?.click()}
            disabled={disabled}
            title="Attach a folder"
            aria-label="Attach a folder"
          >
            <FolderIcon />
          </button>
        </div>

        <textarea
          ref={textarea}
          className="composer__input"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask about a contract clause, risk, or requirement — or attach a document…"
          rows={1}
          aria-label="Message ContractIQ"
          disabled={disabled}
        />

        <button
          type="submit"
          className="composer__send"
          disabled={!canSend}
          aria-label="Send message"
        >
          {busy ? <span className="spinner" aria-hidden /> : <SendIcon />}
        </button>
      </form>

      <input
        ref={filePicker}
        type="file"
        multiple
        accept={ACCEPT_ATTRIBUTE}
        className="visually-hidden"
        tabIndex={-1}
        onChange={(event) => {
          void attach(Array.from(event.target.files ?? []))
          event.target.value = ''
        }}
      />
      <input
        ref={folderPicker}
        type="file"
        multiple
        // `webkitdirectory` turns the picker into a folder picker.
        {...{ webkitdirectory: '' }}
        className="visually-hidden"
        tabIndex={-1}
        onChange={(event) => {
          void attach(Array.from(event.target.files ?? []))
          event.target.value = ''
        }}
      />

      {dragging && <div className="composer__dropzone">Drop files or folders to attach</div>}
    </div>
  )
}

function describeRejection(rejection: RejectedFile): string {
  return `${rejection.name} was skipped — ${rejection.reason}.`
}

function messageOf(reason: unknown): string {
  return reason instanceof Error ? reason.message : 'A document could not be read.'
}

function PaperclipIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden focusable="false">
      <path
        d="M18 8.5v8a5.5 5.5 0 0 1-11 0V6.8a3.6 3.6 0 0 1 7.2 0v9.1a1.7 1.7 0 0 1-3.4 0V8.5H9.3v7.4a3.2 3.2 0 0 0 6.4 0V6.8a5.1 5.1 0 0 0-10.2 0v9.7a7 7 0 0 0 14 0v-8z"
        fill="currentColor"
      />
    </svg>
  )
}

function FolderIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden focusable="false">
      <path
        d="M4 4.5h5.2l2 2H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2z"
        fill="currentColor"
      />
    </svg>
  )
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden focusable="false">
      <path d="M3.4 20.4 21 12 3.4 3.6 3.4 10.2 15.6 12 3.4 13.8z" fill="currentColor" />
    </svg>
  )
}
